'use client';

import { CLIENT_STATIC_FILES_PATH } from 'next/dist/shared/lib/constants';
import Image, { type ImageLoader } from 'next/image';
import { FC, useEffect, useMemo, useState } from 'react';

import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { getAemImageUrl } from './utils/aem-image-loader';
import { type AemImageProps } from './aem-image.types';

export const AemImage: FC<AemImageProps> = ({
  src,
  fallbackSrc,
  width,
  height,
  wid,
  hei,
  smartCropName,
  bfc = 'on',
  fmt = 'png-alpha',
  fit,
  fitUpscale,
  loading,
  fetchPriority,
  priority,
  ...rest
}) => {
  const {
    config: {
      aem: { origin },
    },
  } = usePageContext();

  const [hasError, setHasError] = useState(false);

  const computedSource = useMemo(() => {
    if (!src) return '';

    const dynamicWidth = wid ?? width;
    const dynamicHeight = hei ?? height;

    // Absolute URLs with transformations
    if (typeof src === 'string' && src.startsWith('http')) {
      return getAemImageUrl(src, {
        bfc,
        ...(fmt && { fmt }),
        ...(smartCropName && { smartCropName }),
        ...(dynamicWidth && { wid: dynamicWidth }),
        ...(dynamicHeight && { hei: dynamicHeight }),
        ...(fit && { fit }),
        ...(fitUpscale && { fitUpscale }),
      });
    }

    return src;
  }, [src, bfc, fmt, smartCropName, width, height, fit, hei, wid]);

  useEffect(() => {
    setHasError(false);
  }, [computedSource]);

  // When fetchPriority is 'high' or priority is explicitly set, the image should load eagerly.
  // Passing loading alongside priority causes a Next.js Image error — only one should be used.
  const loadingStrategy = useMemo(() => {
    if (priority || fetchPriority === 'high') {
      return { priority: true as const };
    }

    if (loading) {
      return { loading };
    }

    return {};
  }, [priority, fetchPriority, loading]);

  if (!src && !fallbackSrc) return <div />;

  const imageLoader: ImageLoader = ({ src }) =>
    // return original src if it is absolute url or if it's local image
    // TODO: remove src.startsWith('/web/ui/avis/v1/_next/static/') when there will be no static images
    src.startsWith('http') ||
    src.startsWith(`/_next/${CLIENT_STATIC_FILES_PATH}`)
      ? src
      : origin + src;

  const finalSource = hasError && fallbackSrc ? fallbackSrc : computedSource;

  return (
    <Image
      {...loadingStrategy}
      fetchPriority={fetchPriority}
      data-testid="image-component"
      src={finalSource}
      onError={() => setHasError(true)}
      width={width}
      height={height}
      loader={imageLoader}
      {...rest}
    />
  );
};
